import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOverduePayments, registerPayment } from '../../../Redux/Actions/actions';
import { FaExclamationTriangle, FaDollarSign, FaCalendarAlt, FaUser } from 'react-icons/fa';
import style from './OverduePayments.module.css';

const OverduePayments = () => {
  const dispatch = useDispatch();
  const { overduePayments, rentalsLoading, rentalsError } = useSelector(state => state);

  useEffect(() => {
    dispatch(fetchOverduePayments());
  }, [dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const calculateOverdueDays = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getTotalOverdue = () => {
    return overduePayments.reduce((total, payment) => total + (payment.totalAmount || 0), 0);
  };

  const handleRegisterPayment = (paymentId) => {
    // Esta función se puede expandir con un modal de registro de pago
    console.log('Registrar pago para:', paymentId);
  };

  if (rentalsLoading) {
    return <div className={style.loading}>Cargando pagos vencidos...</div>;
  }

  if (rentalsError) {
    return <div className={style.error}>Error: {rentalsError}</div>;
  }

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaExclamationTriangle className={style.titleIcon} />
          Pagos Vencidos
        </h1>
        
        <div className={style.summary}>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Total Pagos Vencidos:</span>
            <span className={style.summaryValue}>{overduePayments.length}</span>
          </div>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Monto Total Adeudado:</span>
            <span className={`${style.summaryValue} ${style.amount}`}>
              {formatCurrency(getTotalOverdue())}
            </span>
          </div>
        </div>
      </div>

      {overduePayments.length === 0 ? (
        <div className={style.noData}>
          <FaExclamationTriangle className={style.noDataIcon} />
          <h3>¡Excelente!</h3>
          <p>No hay pagos vencidos en este momento</p>
        </div>
      ) : (
        <div className={style.paymentsList}>
          {overduePayments.map((payment) => {
            const overdueDays = calculateOverdueDays(payment.dueDate);
            const isHighPriority = overdueDays > 30;
            
            return (
              <div 
                key={payment.id} 
                className={`${style.paymentCard} ${isHighPriority ? style.highPriority : ''}`}
              >
                <div className={style.cardHeader}>
                  <div className={style.propertyInfo}>
                    <h3 className={style.propertyTitle}>
                      {payment.rental?.property?.title || 'Propiedad sin título'}
                    </h3>
                    <p className={style.propertyAddress}>
                      {payment.rental?.property?.street} {payment.rental?.property?.number}
                    </p>
                  </div>
                  
                  <div className={`${style.priorityBadge} ${isHighPriority ? style.high : style.medium}`}>
                    {isHighPriority ? 'Prioridad Alta' : 'Prioridad Media'}
                  </div>
                </div>

                <div className={style.cardContent}>
                  <div className={style.paymentInfo}>
                    <div className={style.infoItem}>
                      <FaUser className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>Inquilino</span>
                        <span className={style.infoValue}>
                          {payment.rental?.tenants?.find(t => t.tenantType === 'main')?.fullName || 'No asignado'}
                        </span>
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <FaCalendarAlt className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>Fecha Vencimiento</span>
                        <span className={style.infoValue}>
                          {formatDate(payment.dueDate)}
                        </span>
                        <span className={style.overdueDays}>
                          ({overdueDays} días de atraso)
                        </span>
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <FaDollarSign className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>Monto Adeudado</span>
                        <span className={`${style.infoValue} ${style.amount}`}>
                          {formatCurrency(payment.totalAmount)}
                        </span>
                        {payment.lateInterest > 0 && (
                          <span className={style.lateInterest}>
                            + {formatCurrency(payment.lateInterest)} interés
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <span className={style.infoLabel}>Período</span>
                      <span className={style.infoValue}>
                        {formatDate(payment.periodStart)} - {formatDate(payment.periodEnd)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={style.cardActions}>
                  <button 
                    className={`${style.actionButton} ${style.primary}`}
                    onClick={() => handleRegisterPayment(payment.id)}
                  >
                    Registrar Pago
                  </button>
                  <button 
                    className={`${style.actionButton} ${style.secondary}`}
                    onClick={() => window.open(`tel:${payment.rental?.tenants?.find(t => t.tenantType === 'main')?.phone}`)}
                  >
                    Contactar Inquilino
                  </button>
                  <button 
                    className={`${style.actionButton} ${style.secondary}`}
                    onClick={() => console.log('Ver detalle contrato:', payment.rental?.id)}
                  >
                    Ver Contrato
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OverduePayments;